import { createSlice } from '@reduxjs/toolkit';
import _sortBy from 'lodash/sortBy';

import songsDataRaw from 'data/songs.json';
import albumDataRaw from 'data/albums.json';
import toursDataRaw from 'data/tours.json';
import concertsDataRaw from 'data/concerts.json';

import { ToursState, Tour, Concert, Album, Song, Years } from 'interfaces';

const initialStateDefault: ToursState = {
  tours: [],
  tourDetails: null,
  concerts: [],
  festivals: [],
  concertDetails: null,
  setList: [],
  years: [],
  yearsFes: [],
};

const albumsData: Album[] = albumDataRaw as unknown as Album[];
const songsData: Song[] = songsDataRaw as unknown as Song[];
const toursData: Tour[] = toursDataRaw as unknown as Tour[];
const concertsData: Concert[] = concertsDataRaw as unknown as Concert[];

const tourSlice = createSlice({
  name: 'tours',
  initialState: initialStateDefault,
  reducers: {
    loadTours (state) {
      const sortedTours = _sortBy(toursData, 'startDate');

      return {
        ...state,
        tours: sortedTours
      };
    },
    getConcerts (state, action) {
      const { year } = action.payload;

      const concerts = concertsData.map((concert: Concert) => {
        const tourDetails = toursDataRaw.filter((tour) => tour.id === concert.tourId)[0];

        return { ...concert, tourDetails };
      });

      const sortedConcerts = _sortBy(concerts, 'date');

      const years = sortedConcerts.reduce((acc: Years, current: Concert) => {
        const currentYear = current.date.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        return { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], current ] : [ current ] };
      }, {});

      const yearsFes = sortedConcerts.reduce((acc: Years, current: Concert) => {
        const currentYear = current.date.split('-')?.[0];
        const hasYear = (acc?.[currentYear]?.length || 0) > 0;
        return current.tourId === 'festivals-shows-special-events'
          ? { ...acc, [currentYear]: hasYear ? [ ...acc?.[currentYear], current ] : [ current ] }
          : acc;
      }, {});

      return {
        ...state,
        years: year ? { [year]: years[year] } : years,
        yearsFes: year ? { [year]: yearsFes[year] } : yearsFes
      };
    },
    getTourDetails (state, action) {
      const { tourId } = action.payload;
      const tourDetails = toursData.filter((el) => el.id === tourId)[0];
      const concerts = concertsData.filter((el) => el.tourId === tourId);

      const sortedConcerts = _sortBy(concerts, 'date');

      return {
        ...state,
        tourDetails,
        concerts: sortedConcerts
      };
    },
    getConcertDetails (state, action) {
      const { concertId } = action.payload;

      const { songList, ...concertDetails } = concertsData.filter((el) => el.id === concertId)[0];

      const tourDetails = toursData.filter((el) => el.id === concertDetails.tourId)[0];

      const setList = songList.reduce((acc: unknown[], current: string, currentIndex: number, array: string[]) => {
        const song = songsData.filter((el) => el.id === current)[0];

        const albumDetails = song ? albumsData.filter((el) => el.id === song.albumId)[0] : {};
        const isEncore = current === 'encore' || current === 'encore2' || array[currentIndex - 1] === 'encore'|| array[currentIndex - 1] === 'encore2' || (acc[currentIndex-1] as Song)?.isEncore === true;

        if (song === undefined) {
          acc.push(current);
        } else {
          acc.push({
            ...song,
            albumName: albumDetails?.name,
            albumEnglishName: albumDetails?.englishName,
            releaseDate: albumDetails?.releaseDate,
            isEncore
          });
        }
        return acc;
      }, []);

      return {
        ...state,
        ...initialStateDefault,
        tourDetails,
        concertDetails: {
          ...initialStateDefault.concertDetails,
          ...concertDetails
        },
        setList
      };
    }
  }
});

export const { loadTours, getConcerts, getTourDetails, getConcertDetails } = tourSlice.actions;

export default tourSlice.reducer;
